import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { resourceStatusLabel } from '@isbd/shared';
import { kuunda } from '@/lib/kuunda';
import { useAuth } from '@/features/auth/AuthProvider';
import { useToast } from '@/features/ui/toast/useToast';
import './pilotage.css';

type Resource = {
  id: string;
  kind: 'humain' | 'materiel';
  label: string;
  role_or_type: string;
  quantity: number;
  unit: string;
  cost: number;
  status: string;
  notes: string;
};

type BudgetSummary = { income: number; expense: number; balance: number; count: number };

interface Props {
  projectId: string;
}

const emptyForm = {
  kind: 'humain' as 'humain' | 'materiel',
  label: '',
  role_or_type: '',
  quantity: 1,
  unit: '',
  cost: 0,
  status: 'disponible',
  notes: '',
};

export function ResourcesPanel({ projectId }: Props) {
  const { profile } = useAuth();
  const toast = useToast();
  const [resources, setResources] = useState<Resource[]>([]);
  const [budget, setBudget] = useState<BudgetSummary>({ income: 0, expense: 0, balance: 0, count: 0 });
  const [form, setForm] = useState(emptyForm);
  const [tab, setTab] = useState<'humain' | 'materiel'>('humain');
  const [editingId, setEditingId] = useState<string | null>(null);

  async function load() {
    if (!projectId) return;
    const [res, lines] = await Promise.all([
      kuunda.from('project_resources').select('*').eq('project_id', projectId).order('created_at', { ascending: false }),
      kuunda.from('budget_lines').select('amount, kind').eq('project_id', projectId),
    ]);
    if (res.error) toast.error(res.error.message);
    else setResources((res.data ?? []) as Resource[]);

    let income = 0;
    let expense = 0;
    for (const l of lines.data ?? []) {
      if (l.kind === 'income') income += Number(l.amount);
      else expense += Number(l.amount);
    }
    setBudget({ income, expense, balance: income - expense, count: (lines.data ?? []).length });
  }

  useEffect(() => {
    void load();
  }, [projectId]);

  const filtered = useMemo(() => resources.filter((r) => r.kind === tab), [resources, tab]);

  function startEdit(r: Resource) {
    setEditingId(r.id);
    setForm({
      kind: r.kind,
      label: r.label,
      role_or_type: r.role_or_type,
      quantity: Number(r.quantity),
      unit: r.unit,
      cost: Number(r.cost),
      // "Alloué" a été retiré du formulaire : on bascule vers une valeur existante si besoin.
      status: ['disponible', 'a_recruter', 'indisponible'].includes(r.status) ? r.status : 'disponible',
      notes: r.notes,
    });
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!profile || !projectId || !form.label.trim()) return;
    if (editingId) {
      const { error: err } = await kuunda.from('project_resources').update({
        ...form,
      }).eq('id', editingId);
      if (err) toast.error(err.message);
      else {
        toast.success('Ressource mise à jour.');
        setEditingId(null);
        setForm({ ...emptyForm, kind: tab });
        await load();
      }
      return;
    }

    const { error: err } = await kuunda.from('project_resources').insert({
      user_id: profile.id,
      project_id: projectId,
      ...form,
    });
    if (err) toast.error(err.message);
    else {
      toast.success('Ressource ajoutée.');
      setForm({ ...emptyForm, kind: tab });
      await load();
    }
  }

  async function remove(id: string) {
    if (!window.confirm('Supprimer cette ressource ?')) return;
    await kuunda.from('project_resources').delete().eq('id', id);
    await load();
  }

  return (
    <div>
      <section className="panel" style={{ marginBottom: '1rem' }}>
        <h3 style={{ marginTop: 0 }}>Ressources financières</h3>
        <p className="muted" style={{ marginTop: 0 }}>
          Suivi budgétaire du projet (recettes, dépenses, solde).
        </p>
        <div className="pilotage-resource-kpis">
          <div>
            <span className="muted">Recettes</span>
            <strong>{budget.income.toLocaleString('fr-FR')}</strong>
          </div>
          <div>
            <span className="muted">Dépenses</span>
            <strong>{budget.expense.toLocaleString('fr-FR')}</strong>
          </div>
          <div>
            <span className="muted">Solde</span>
            <strong>{budget.balance.toLocaleString('fr-FR')}</strong>
          </div>
          <div>
            <span className="muted">Lignes</span>
            <strong>{budget.count}</strong>
          </div>
        </div>
        <Link className="btn btn-primary" to="/app/budget">
          Gérer le budget détaillé
        </Link>
      </section>

      <section className="panel">
        <div className="pilotage-section-head">
          <h3 style={{ marginTop: 0 }}>Ressources humaines & matérielles</h3>
          <div className="pilotage-tabs">
            <button
              type="button"
              className={tab === 'humain' ? 'btn btn-primary' : 'btn btn-ghost'}
              onClick={() => {
                setTab('humain');
                setEditingId(null);
                setForm((f) => ({ ...f, kind: 'humain' }));
              }}
            >
              Humaines
            </button>
            <button
              type="button"
              className={tab === 'materiel' ? 'btn btn-primary' : 'btn btn-ghost'}
              onClick={() => {
                setTab('materiel');
                setEditingId(null);
                setForm((f) => ({ ...f, kind: 'materiel' }));
              }}
            >
              Matérielles
            </button>
          </div>
        </div>

        <form className="pilotage-resource-form" onSubmit={(e) => void onSubmit(e)}>
          <div className="field">
            <label>{tab === 'humain' ? 'Nom / fonction' : 'Équipement / bien'}</label>
            <input
              required
              value={form.label}
              onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
            />
          </div>
          <div className="field">
            <label>{tab === 'humain' ? 'Rôle' : 'Type'}</label>
            <input
              value={form.role_or_type}
              onChange={(e) => setForm((f) => ({ ...f, role_or_type: e.target.value }))}
            />
          </div>
          <div className="field">
            <label>Quantité</label>
            <input
              type="number"
              min={0}
              step={0.01}
              value={form.quantity}
              onChange={(e) => setForm((f) => ({ ...f, quantity: Number(e.target.value) }))}
            />
          </div>
          <div className="field">
            <label>Unité</label>
            <input
              value={form.unit}
              placeholder={tab === 'humain' ? 'ETP, jours…' : 'unité, m²…'}
              onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))}
            />
          </div>
          <div className="field">
            <label>Coût estimé</label>
            <input
              type="number"
              min={0}
              step={0.01}
              value={form.cost}
              onChange={(e) => setForm((f) => ({ ...f, cost: Number(e.target.value) }))}
            />
          </div>
          <div className="field">
            <label>Statut</label>
            <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}>
              <option value="disponible">Disponible</option>
              <option value="a_recruter">À recruter / acquérir</option>
              <option value="indisponible">Indisponible</option>
            </select>
          </div>
          <div className="field" style={{ gridColumn: '1 / -1' }}>
            <label>Notes</label>
            <textarea rows={2} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
          </div>
          <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {editingId ? (
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => {
                  setEditingId(null);
                  setForm({ ...emptyForm, kind: tab });
                }}
              >
                Annuler
              </button>
            ) : null}
            <button type="submit" className="btn btn-primary">
              {editingId ? 'Enregistrer' : 'Ajouter'}
            </button>
          </div>
        </form>

        {filtered.length === 0 ? (
          <p className="u-empty">Aucune ressource {tab === 'humain' ? 'humaine' : 'matérielle'}.</p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Libellé</th>
                  <th>{tab === 'humain' ? 'Rôle' : 'Type'}</th>
                  <th>Qté</th>
                  <th>Coût</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id}>
                    <td>{r.label}</td>
                    <td>{r.role_or_type || '—'}</td>
                    <td>
                      {r.quantity} {r.unit}
                    </td>
                    <td>{Number(r.cost).toLocaleString('fr-FR')}</td>
                    <td>{resourceStatusLabel(r.status)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          className="btn btn-ghost"
                          onClick={() => {
                            setTab(r.kind);
                            startEdit(r);
                          }}
                          disabled={editingId === r.id}
                        >
                          Modif.
                        </button>
                        <button type="button" className="btn btn-ghost" onClick={() => void remove(r.id)}>
                          Suppr.
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
